/*
* Palo Alto Theme
*
* Use this file to add custom Javascript to Palo Alto.  Keeping your custom
* Javascript in this fill will make it easier to update Palo Alto. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/

(function (Flickity) {
  class CustomCarousel extends HTMLElement {
    constructor() {
      super();

      this.slider = null;
      this.started = false;

      this.settings = {
        autoplay: this.hasAttribute('autoplay'),
        autoplaySpeed: Number(this.dataset.autoplaySpeed) || 4000,
        loop: this.hasAttribute('loop'),
      };

      this.resizeHandler = this.resize.bind(this);
    }

    connectedCallback() {
      this.observe();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.resizeHandler);

      if (this.slider) {
        this.slider.destroy();
        this.slider = null;
      }
    }

    observe() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.started) {
              this.started = true;

              this.init();

              observer.disconnect();
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '200px 0px'
        }
      );

      observer.observe(this);
    }

    init() {
      if (this.children.length <= 1) return;

      this.slider = new Flickity(this, {
        watchCSS: true,
        cellAlign: 'left',
        contain: true,

        // Flickity calculates based on actual cell width
        groupCells: false,

        prevNextButtons: true,
        pageDots: false,

        wrapAround: this.settings.loop,

        autoPlay: this.settings.autoplay
          ? this.settings.autoplaySpeed
          : false,

        pauseAutoPlayOnHover: true,

        adaptiveHeight: false,

        on: {
          ready: () => {
            this.dispatchEvent(
              new CustomEvent('slider:ready')
            );
          },

          change: (index) => {
            this.dispatchEvent(
              new CustomEvent('slider:change', {
                detail: {
                  index
                }
              })
            );
          }
        }
      });

      window.addEventListener(
        'resize',
        this.resizeHandler
      );
    }

    resize() {
      if (!this.slider) return;
      this.slider.resize();
    }

    next() {
      if (!this.slider) return;
      this.slider.next();
    }

    previous() {
      if (!this.slider) return;
      this.slider.previous();
    }

    select(index) {
      if (!this.slider) return;
      this.slider.select(index);
    }
  }

  if (!customElements.get('custom-carousel')) {
    customElements.define(
      'custom-carousel',
      CustomCarousel
    );
  }
})(themeVendor.Flickity);

(function() {
  // Add custom code below this line

  class DeliveryDate extends HTMLElement {
    constructor() {
      super();

      this.lowerThreshold = parseInt(this.dataset.lowerThreshold) ?? 0;
      this.higherThreshold = parseInt(this.dataset.higherThreshold) ?? 1;
    }

    connectedCallback() {
      this.init();
    }

    init() {
      const currentDate = new Date();
      const lowerThresholdDate = this.addBusinessDays(currentDate, this.lowerThreshold);
      const higherThresholdDate = this.addBusinessDays(currentDate, this.higherThreshold)

      const formattingOptions = {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
      }
      const formattedLowerThresholdDate = new Intl.DateTimeFormat(`${Shopify.locale}-${Shopify.country}`, formattingOptions).format(lowerThresholdDate).replaceAll(',', '');
      const formattedHigherThresholdDate = new Intl.DateTimeFormat(`${Shopify.locale}-${Shopify.country}`, formattingOptions).format(higherThresholdDate).replaceAll(',', '');
      
      this.textContent = `${Shopify.locale == 'de' 
        ? formattedLowerThresholdDate.slice(0,-1) : formattedLowerThresholdDate} - ${Shopify.locale == 'de' 
        ? formattedHigherThresholdDate.slice(0,-1) : formattedHigherThresholdDate}`;
    }

    addBusinessDays(startDate, daysToAdd) {
      let currentDate = new Date(startDate.getTime()); // Create a copy to avoid modifying the original
      let addedDays = 0;

      while (addedDays < daysToAdd) {
        currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
        const dayOfWeek = currentDate.getDay(); // 0 for Sunday, 6 for Saturday

        // Check if it's a weekday (Monday to Friday) or Saturday
        if (dayOfWeek !== 0) {
          addedDays++;
        }
      }
      return currentDate;
    }
  }


  if (!customElements.get('delivery-date')) {
    customElements.define('delivery-date', DeliveryDate);
  }

  class ChatBubble extends HTMLElement {
    constructor() {
      super();

      this.sectionId = this.dataset.sectionId;
      this.elements = {
        togglers: this.querySelectorAll('[data-toggle-chat-bubble]'),
        openers: this.querySelectorAll('[data-open-chat-bubble]'),
        closers: this.querySelectorAll('[data-close-chat-bubble]'),
      };
      this.opened = false;
    }

    connectedCallback() {
      this.init();
    }
    
    init() {
      var _this = this;
      if (window.Shopify.designMode) {
        document.addEventListener('shopify:section:select', function (event) {
          if (event.detail.sectionId == _this.sectionId) {
            _this.open();
          }
        });

        document.addEventListener('shopify:section:deselect', function (event) {
          if (event.detail.sectionId == _this.sectionId) {
            _this.close();
          }
        });
      }

      if (this.elements.togglers) {
        this.elements.togglers.forEach((button) => 
          button.addEventListener('click', this.toggle.bind(this))
        );
      }

      if (this.elements.openers) {
        this.elements.openers.forEach((button) => 
          button.addEventListener('click', this.open.bind(this))
        );
      }

      if (this.elements.closers) {
        this.elements.closers.forEach((button) => 
          button.addEventListener('click', this.close.bind(this))
        );
      }
    }

    toggle() {
      if (this.opened) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      this.setAttribute('aria-expanded', true);
      this.opened = true;
    }

    close() {
      this.setAttribute('aria-expanded', false);
      this.opened = false;
    }
  }

  if (!customElements.get('chat-bubble')) {
    customElements.define('chat-bubble', ChatBubble);
  }

  class CustomVideo extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        video: this.querySelector('video'),
        playButton: this.querySelector('[data-video-play]'),
      };
    }

    connectedCallback() {
      this.init();
    }

    init() {
      if (!this.elements.video) return;

      if (this.elements.playButton) {
        this.elements.playButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.elements.video.play();
        });
      }

      this.elements.video.addEventListener('play', () => {
        this.dataset.isPlaying = 'true';
      });

      this.elements.video.addEventListener('pause', () => {
        this.dataset.isPlaying = 'false';
      })
    }
  }

  if (!customElements.get('custom-video')) {
    customElements.define('custom-video', CustomVideo);
  }

  class ProductDiscount extends HTMLElement {
    constructor() {
      super();

      this.discountValue = parseFloat(this.dataset.discountValue);
      this.copied = this.dataset.copied;
      this.code = this.dataset.code;

      this.elements = {
        moneySaved: this.querySelector('[data-money-saved]'),
        copyToClipboardButton: this.querySelector('[data-copy-to-clipboard]'),
      };
    }

    connectedCallback() {
      this.init();
    }

    init() {
      if(this.elements.moneySaved) {
        window.addEventListener('theme:variant:change', this.onVariantChange.bind(this))
      }

      if (this.elements.copyToClipboardButton) {
        this.elements.copyToClipboardButton.addEventListener('click', this.handleCopyToClipboardButtonClick.bind(this));
      }
    }

    onVariantChange(event) {
      const {variant} = event.detail;
      const {price} = variant;
      const moneySaved = price * this.discountValue;
      this.elements.moneySaved.innerHTML = themeVendor.themeCurrency.formatMoney(moneySaved, theme.moneyFormat);
    }

    handleCopyToClipboardButtonClick() {
      navigator.clipboard.writeText(this.code);
      this.copied = true;
      this.dataset.copied = this.copied;

      setTimeout(() => {
        this.copied = false;
        this.dataset.copied = this.copied;
      }, 3000);
    }
  }

  if (!customElements.get('product-discount')) {
    customElements.define('product-discount', ProductDiscount);
  }

  class CartDiscount extends HTMLElement {
    constructor() {
      super();
      
      this.state = this.dataset.state; // idle, submitted, success, error;
      this.loading = false;
      const config = JSON.parse(this.querySelector('[data-config]').innerHTML);
      this.cartState = config.cartState;
      this.strings = config.strings;

      this.elements = {
        removeCodeButtons: this.querySelectorAll('[data-remove-code]'),
        discountsList: this.querySelector('[data-discounts-list]'),
        error: this.querySelector('[data-error]'),
        errorText: this.querySelector('[data-error-text]'),
        form: this.querySelector('form'),
      };

      if (this.parentNode && this.parentNode.hasAttribute('data-parent')) {
        this.parent = this.parentNode;
      }
    }

    connectedCallback() {
      this.init();
    }

    init() {
      if (this.elements.form) {
        this.elements.form.addEventListener('submit', this.handleFormSubmit.bind(this));
      }

      if (this.elements.removeCodeButtons) {
        this.attachRemoveCodeButtonsListeners();
      }

      document.addEventListener('theme:cart:update', this.handleCartUpdate.bind(this));
      document.addEventListener('theme:cart:added', this.handleCartUpdate.bind(this));
    }

    disconnectedCallback() {
      if (this.elements.form) {
        this.elements.form.removeEventListener('submit', this.handleFormSubmit.bind(this));
      }

      if (this.elements.removeCodeButtons) {
        this.removeRemoveCodeButtonsListeners();
      }

      document.removeEventListener('theme:cart:update', this.handleCartUpdate.bind(this));
      document.removeEventListener('theme:cart:added', this.handleCartUpdate.bind(this));
    }

    attachRemoveCodeButtonsListeners() {
      this.elements.removeCodeButtons.forEach((button) => {
        button.addEventListener('click', this.handleRemoveCodeButtonClick.bind(this));
      })
    }

    removeRemoveCodeButtonsListeners() {
      this.elements.removeCodeButtons.forEach((button) => {
        button.removeEventListener('click', this.handleRemoveCodeButtonClick.bind(this));
      })
    }

    handleCartUpdate(event) {
      this.reload();
    }

    handleRemoveCodeButtonClick(event) {
      event.preventDefault();
      let _this = this;
      const codeToRemove = event.target.dataset.removeCode;
      const discountCodes = this.cartState.discount_applications.map((discount) => discount.title);
      const newDiscountCodes = discountCodes.filter((discount) => discount.title !== codeToRemove).join(',');

      const formData = {
        discount: newDiscountCodes,
      }

      fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })
      .then(response => response.json())
      .then(data => {
        window.cart.getCart();
        _this.reload();
      });
    }

    handleFormSubmit(event) {
      event.preventDefault();
      let _this = this;
      const newDiscountCode = this.elements.form.discount.value
      const discountCodes = this.cartState.discount_applications.map((discount) => discount.title).concat(newDiscountCode).join(',');

      this.updateState('submitted');

      const formData = {
        discount: newDiscountCode,
      };

      fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })
      .then(response => response.json())
      .then(data => {
        const matchingDiscountCode = data.discount_codes.find((discount) => discount.code == newDiscountCode);

        if (matchingDiscountCode.applicable) {
          _this.updateState('success');
          window.cart.getCart();
          _this.reload(true);
        } else {
          _this.elements.errorText.innerHTML = _this.strings.error.replaceAll('[code]', newDiscountCode);
          _this.updateState('error');
        }
      });
    }

    updateState(state) {
      this.state = state;
      this.dataset.state = state;
    }

    handleErrors(response) {
      if (!response.ok) {
        return response.json().then(function (json) {
          const e = new FetchError({
            status: response.statusText,
            headers: response.headers,
            json: json,
          });
          throw e;
        });
      }
      return response;
    }

    updateSelf(element) {
      const discountsList = element.querySelector('[data-discounts-list]');
      this.elements.discountsList.innerHTML = discountsList.innerHTML;
      this.elements.removeCodeButtons = this.querySelectorAll('[data-remove-code]');
      if (this.elements.removeCodeButtons) {
        this.attachRemoveCodeButtonsListeners();
      }
      const config = JSON.parse(element.querySelector('[data-config]').innerHTML);
      this.cartState = config.cartState;
      if (this.parent) {
        this.cartState.item_count > 0 ? this.parent.classList.remove('hidden') : this.parent.classList.add('hidden');
      }
      this.loading = false;
    }

    reload(reset = false) {
      let _this = this;
      if (this.loading) return;
      this.loading = true;

      fetch(theme.routes.cart_url + '?section_id=api-cart-discount')
        .then(_this.handleErrors)
        .then((response) => response.text())
        .then((response) => {
          const element = document.createElement('div');
          element.innerHTML = response;
          _this.updateSelf(element);
          if (reset) {
            setTimeout(() => {
              _this.updateState('idle');
              _this.elements.form.discount.value = '';
            }, 5000);
          }
        })
        .catch((error) => console.log(error));
    }
  }

  if (!customElements.get('cart-discount')) {
    customElements.define('cart-discount', CartDiscount);
  }

  class CounterProgress extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        counter: this.querySelector('[data-counter]'),
        progressFill: this.querySelector('[data-progress-fill]')
      };

      this.settings = {
        counterStart: Number(this.dataset.counterStart),
        counterEnd: Number(this.dataset.counterEnd),

        progressStart: Number(this.dataset.progressStart),
        progressEnd: Number(this.dataset.progressEnd),
        progressMax: Number(this.dataset.progressMax),

        duration: Number(this.dataset.duration) || 2000,
      };

      this.started = false;
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.observe();
    }

    observe() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.started) {
              this.started = true;

              this.animateCounter();
              this.animateProgress();

              observer.disconnect();
            }
          });
        },
        {
          threshold: 0.3
        }
      );
      observer.observe(this);
    }


    animateCounter() {
      if (!this.elements.counter) return;

      const start = this.settings.counterStart;
      const end = this.settings.counterEnd;

      const formatter = new Intl.NumberFormat('de-DE');

      this.animateValue(
        start,
        end,
        (value) => {
          this.elements.counter.textContent =
            formatter.format(Math.floor(value));
        }
      );

    }

    animateProgress() {
      if (!this.elements.progressFill) return;

      const start = this.settings.progressStart;
      const end = this.settings.progressEnd;
      const max = this.settings.progressMax;

      this.animateValue(
        start,
        end,
        (value) => {
          const percent = Math.min(
            (value / max) * 100,
            100
          );

          this.elements.progressFill.style.width =
            `${percent}%`;
        }
      );
    }

    animateValue(start, end, callback) {
      let startTime = null;
      const animate = (time) => {
        if (!startTime) {
          startTime = time;
        }

        const progress = Math.min(
          (time - startTime) / this.settings.duration,
          1
        );

        const eased =
          1 - Math.pow(1 - progress, 3);

        const value =
          start + (end - start) * eased;

        callback(value);

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }
  }

  if (!customElements.get('counter-progress')) {
    customElements.define(
      'counter-progress',
      CounterProgress
    );
  }

  // ^^ Keep your scripts inside this IIFE function call to
  // avoid leaking your variables into the global scope.
})();
